import { Download, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useQuery } from "@tanstack/react-query";

import {
  type OrderCard,
  type OrderKind,
  downloadAssemblyTask,
  getOrderCheckConfig,
  listOrderOrganizations,
  listOrderStates,
  listOrders,
  parseKind,
} from "@/api/order_check";
import { OrdersList } from "@/pages/order-check/OrdersList";
import { extractErrorMessage } from "@/lib/api-errors";

/** Проверка заказов — список заказов покупателя для сборки.
 *
 * Сам список общий с разделом раскладки (`OrdersList`): фильтры, поиск и
 * подгрузка там одни и те же, а различаются ручки, куда ведёт тап и что
 * нарисовано справа на карточке. */
const KIND_KEY = "wh_oc_kind";

const KIND_TABS: { value: OrderKind; label: string }[] = [
  { value: "customer", label: "Заказы" },
  { value: "transfer", label: "На фулфилмент" },
];

function AssemblyTaskButton({
  order,
  kind,
}: {
  order: OrderCard;
  kind: OrderKind;
}): React.ReactElement {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        setBusy(true);
        try {
          await downloadAssemblyTask(order.ref, kind);
        } catch (err) {
          toast.error(extractErrorMessage(err, "Не удалось скачать сборочное задание"));
        } finally {
          setBusy(false);
        }
      }}
      disabled={busy}
      title="Сборочное задание (DOCX)"
      aria-label="Сборочное задание"
      className="flex size-10 shrink-0 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-accent disabled:opacity-50"
    >
      {busy ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
    </button>
  );
}

export function OrderCheckListPage(): React.ReactElement {
  const navigate = useNavigate();
  // Вид помнится между заходами: приёмщик фулфилмента работает только с
  // перемещениями, и переставлять вкладку после каждого обновления страницы —
  // ровно та возня, от которой экран должен избавлять.
  const [kind, setKind] = useState<OrderKind>(() => {
    try {
      return parseKind(localStorage.getItem(KIND_KEY));
    } catch {
      // Заблокированные данные сайта БРОСАЮТ на чтении. Незапомненный вид —
      // мелочь, а упасть тут значит не отрисовать страницу вовсе.
      return "customer";
    }
  });
  // Тот же ключ и то же время жизни, что у остальных читателей конфига
  // (экраны сессии и паллеты): своя запись перезапрашивала бы `/config` на
  // каждом заходе и не грелась бы от соседей.
  const cfg = useQuery({
    queryKey: ["oc-config"],
    queryFn: getOrderCheckConfig,
    staleTime: 300_000,
  });

  function pick(next: OrderKind) {
    setKind(next);
    try {
      localStorage.setItem(KIND_KEY, next);
    } catch {
      // Приватный режим — вид просто не запомнится.
    }
  }

  // ⚠️ Склады-фулфилменты не названы — переключателя НЕТ, и вид всегда
  // «Заказы»: вкладка, которая заведомо пуста, читается как поломка раздела.
  // Запомненный `transfer` в этом случае игнорируем — иначе человек остался бы
  // на пустом списке без способа вернуться.
  const enabled = cfg.data?.fulfilment_enabled ?? false;
  const active: OrderKind = enabled ? kind : "customer";

  return (
    <OrdersList
      key={active}
      title="Проверка заказов"
      lsPrefix={active === "transfer" ? "wh_oc_tr" : "wh_oc"}
      queryKey={active === "transfer" ? "order-check-tr" : "order-check"}
      orgsKey="order-check"
      tabs={
        enabled ? (
          <div className="flex rounded-md border p-0.5">
            {KIND_TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => pick(t.value)}
                className={`flex-1 rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                  active === t.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        ) : undefined
      }
      api={{
        list: (p) => listOrders({ ...p, kind: active }),
        organizations: () => listOrderOrganizations(),
        states: () => listOrderStates(active),
      }}
      onOpen={(o) =>
        navigate(
          active === "transfer"
            ? `/order-check/orders/${o.ref}?kind=transfer`
            : `/order-check/orders/${o.ref}`,
        )
      }
      actions={(o) => <AssemblyTaskButton order={o} kind={active} />}
    />
  );
}
